import { env } from '@/config/env'

import type { KeycloakTokenResponse } from './keycloakLegacyBridgeLogin'

/**
 * Turns a refresh token into a live session, straight from the browser.
 *
 * The public client has no secret, which is precisely why this can be done here and why the
 * hand-off can carry a refresh token rather than needing the API to mint anything: the token
 * the platform parked is the same token this browser would have refreshed with had it been on
 * the platform's origin.
 *
 * Impersonation sessions are the exception — they belong to the confidential exchange client
 * and refresh through the API — but a hand-off is somebody entering their own space, so the
 * exception cannot arise here.
 */
export const refreshWithPublicClient = async (
  refreshToken: string
): Promise<KeycloakTokenResponse | null> => {
  const url = env.KEYCLOAK_URL
  const realm = env.KEYCLOAK_REALM
  const clientId = env.KEYCLOAK_CLIENT_ID

  if (!url || !realm || !clientId) {
    return null
  }

  const response = await fetch(
    `${url.replace(/\/$/, '')}/realms/${realm}/protocol/openid-connect/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      }),
    }
  )

  if (!response.ok) {
    return null
  }

  return (await response.json()) as KeycloakTokenResponse
}
